import { useMemo, useState } from "react";
import clsx from "clsx";
import { Button } from "@/components/ui/Button";
import { Spinner } from "@/components/ui/Spinner";
import { formatHours } from "@/lib/bookedHours";
import { formatEuro } from "@/lib/formatMoney";
import { formatVorlageGueltigAb } from "@/lib/gdVorlagen";
import type { AuftragEinkaufArtikel, DokumentTyp } from "@/lib/dokumente";
import {
  POSITION_BLOCK_META,
  POSITION_DRAG_TYPE,
  POSITION_EINKAUF_ARTIKEL_DRAG_TYPE,
  POSITION_GEBUCHTE_STUNDEN_DRAG_TYPE,
  type EinkaufArtikelDragPayload,
  type GebuchteStundenDragPayload,
  type PaletteBlockTyp,
} from "@/pages/dokumente/documentPositionModel";
import {
  filterGdTemplates,
  type GdDocumentTemplateListItem,
} from "@/pages/dokumente/documentTemplateModel";
import { vkFromEk } from "@/pages/dokumente/documentPricing";

export const POSITION_TEMPLATE_DRAG_TYPE = "application/x-eh-gd-template-id";
export const POSITION_TEMPLATE_REORDER_DRAG_TYPE = "application/x-eh-gd-template-reorder";

const BLOCK_TYPES: PaletteBlockTyp[] = ["artikel", "leistung", "nachweisarbeiten", "titel", "text"];

interface DocumentBuilderSidebarProps {
  dokumentTyp: DokumentTyp;
  templates: GdDocumentTemplateListItem[];
  templatesLoading: boolean;
  applyingTemplateId: number | null;
  templatesEnabled?: boolean;
  auftragId?: number | null;
  einkaufArtikel: AuftragEinkaufArtikel[];
  einkaufLoading: boolean;
  aufschlagProzent: number;
  onAufschlagChange: (value: number) => void;
  gebuchteStundenTotal: number | null;
  gebuchteStundenLoading: boolean;
  onInsertBlock: (typ: PaletteBlockTyp) => void;
  onPaletteDragStart: (typ: PaletteBlockTyp) => void;
  onPaletteDragEnd: () => void;
  onCopyDragStart: () => void;
  onApplyTemplate: (templateId: number, replace: boolean) => void;
  onDeleteTemplate: (templateId: number) => void;
  onEditTemplate?: (templateId: number) => void;
  onReorderTemplates?: (orderedIds: number[]) => void;
  onSaveTemplate: () => void;
  onInsertEinkaufArtikel: (item: AuftragEinkaufArtikel) => void;
  onInsertGebuchteStunden: () => void;
  onOpenHistorischePositionen: () => void;
}

function parseAufschlagInput(raw: string): number | null {
  const s = raw.trim().replace(/\s/g, "").replace(",", ".");
  if (!s) return null;
  const n = Number(s);
  return Number.isFinite(n) && n >= 0 ? n : null;
}

function formatMenge(item: AuftragEinkaufArtikel): string {
  if (item.menge == null) return "1";
  const qty = Number(item.menge).toLocaleString("de-DE", { maximumFractionDigits: 2 });
  const unit = (item.einheit || "").trim();
  if (!unit || unit === "H87") return qty;
  return `${qty} ${unit}`;
}

export function DocumentBuilderSidebar({
  dokumentTyp,
  templates,
  templatesLoading,
  applyingTemplateId,
  templatesEnabled = true,
  auftragId,
  einkaufArtikel,
  einkaufLoading,
  aufschlagProzent,
  onAufschlagChange,
  gebuchteStundenTotal,
  gebuchteStundenLoading,
  onInsertBlock,
  onPaletteDragStart,
  onPaletteDragEnd,
  onCopyDragStart,
  onApplyTemplate,
  onDeleteTemplate,
  onEditTemplate,
  onReorderTemplates,
  onSaveTemplate,
  onInsertEinkaufArtikel,
  onInsertGebuchteStunden,
  onOpenHistorischePositionen,
}: DocumentBuilderSidebarProps) {
  const [templateQuery, setTemplateQuery] = useState("");
  const [reorderFromId, setReorderFromId] = useState<number | null>(null);
  const [reorderOverId, setReorderOverId] = useState<number | null>(null);

  const typLabel =
    dokumentTyp === "angebot"
      ? "Angebote"
      : dokumentTyp === "rechnung"
        ? "Rechnungen"
        : dokumentTyp === "lieferschein"
          ? "Lieferscheine"
          : "Auftragsbestätigungen";

  const showEinkauf = dokumentTyp === "rechnung" && auftragId != null && auftragId > 0;
  const showGebuchteStunden =
    dokumentTyp === "angebot" && auftragId != null && auftragId > 0;
  const hasGebuchteStunden =
    gebuchteStundenTotal != null && Number.isFinite(gebuchteStundenTotal) && gebuchteStundenTotal > 0;
  const filteredTemplates = useMemo(
    () => filterGdTemplates(templates, templateQuery),
    [templateQuery, templates],
  );

  function buildEinkaufDragPayload(item: AuftragEinkaufArtikel): string {
    const payload: EinkaufArtikelDragPayload = { item, aufschlagProzent };
    return JSON.stringify(payload);
  }

  function commitReorder(targetId: number) {
    if (reorderFromId == null || reorderFromId === targetId || !onReorderTemplates) {
      setReorderFromId(null);
      setReorderOverId(null);
      return;
    }
    const ids = templates.map((t) => t.id);
    const from = ids.indexOf(reorderFromId);
    const to = ids.indexOf(targetId);
    if (from < 0 || to < 0) {
      setReorderFromId(null);
      setReorderOverId(null);
      return;
    }
    const next = [...ids];
    const [moved] = next.splice(from, 1);
    if (moved == null) {
      setReorderFromId(null);
      setReorderOverId(null);
      return;
    }
    next.splice(to, 0, moved);
    onReorderTemplates(next);
    setReorderFromId(null);
    setReorderOverId(null);
  }

  return (
    <aside className="gd-builder-sidebar">
      <section className="gd-palette">
        <p className="gd-palette__title">Bausteine</p>
        {BLOCK_TYPES.map((typ) => {
          const meta = POSITION_BLOCK_META[typ];
          return (
            <button
              key={typ}
              type="button"
              className="gd-palette-item"
              draggable
              onDragStart={(e) => {
                onPaletteDragStart(typ);
                e.dataTransfer.setData(POSITION_DRAG_TYPE, typ);
                e.dataTransfer.effectAllowed = "copyMove";
              }}
              onDragEnd={onPaletteDragEnd}
              onClick={() => onInsertBlock(typ)}
            >
              <span className="gd-palette-item__icon">{meta.icon}</span>
              <span>
                <span className="block text-sm font-semibold text-label">{meta.label}</span>
                <span className="mt-0.5 block text-xs text-label-secondary">{meta.hint}</span>
              </span>
            </button>
          );
        })}
      </section>

      <section className="gd-palette">
        <p className="gd-palette__title">Frühere Positionen</p>
        <p className="gd-palette__hint">
          Alle jemals in Dokumenten erstellten Positionen durchsuchen und in dieses Dokument übernehmen.
        </p>
        <Button type="button" variant="secondary" className="w-full" onClick={onOpenHistorischePositionen}>
          Positionen durchsuchen
        </Button>
      </section>

      {templatesEnabled ? (
        <section className="gd-palette gd-palette--templates">
          <div className="gd-palette__header">
            <p className="gd-palette__title">Vorlagen</p>
            <Button type="button" size="sm" variant="secondary" onClick={onSaveTemplate}>
              Als Vorlage speichern
            </Button>
          </div>
          <p className="gd-palette__hint">
            Wiederkehrende Angebote als Vorlage ablegen. Klick fügt Positionen hinzu, Shift+Klick ersetzt
            den Inhalt. Per Drag einfügen oder in der Liste sortieren.
          </p>

          {templates.length > 0 ? (
            <input
              type="search"
              className="eh-input mb-2"
              value={templateQuery}
              onChange={(e) => setTemplateQuery(e.target.value)}
              placeholder="Vorlagen suchen …"
              aria-label="Vorlagen suchen"
            />
          ) : null}

          {templatesLoading ? (
            <div className="flex items-center gap-2 py-4 text-sm text-label-secondary">
              <Spinner className="h-4 w-4" />
              Vorlagen werden geladen …
            </div>
          ) : templates.length === 0 ? (
            <p className="py-3 text-sm text-label-secondary">Noch keine Vorlagen für {typLabel}.</p>
          ) : filteredTemplates.length === 0 ? (
            <p className="py-3 text-sm text-label-secondary">Keine Vorlagen für diese Suche.</p>
          ) : (
            <ul className="gd-template-list">
              {filteredTemplates.map((item) => {
                const busy = applyingTemplateId === item.id;
                const folderCount = (item.ordner_ids?.length || 0) + (item.sordner_ids?.length || 0);
                return (
                  <li
                    key={item.id}
                    className={clsx(
                      "gd-template-list__item",
                      reorderOverId === item.id && "is-reorder-over",
                    )}
                    onDragOver={(e) => {
                      if (reorderFromId == null) return;
                      e.preventDefault();
                      e.dataTransfer.dropEffect = "move";
                      setReorderOverId(item.id);
                    }}
                    onDrop={(e) => {
                      const reorderRaw = e.dataTransfer.getData(POSITION_TEMPLATE_REORDER_DRAG_TYPE);
                      if (!reorderRaw) return;
                      e.preventDefault();
                      e.stopPropagation();
                      commitReorder(item.id);
                    }}
                  >
                    <button
                      type="button"
                      className={clsx(
                        "gd-template-item",
                        busy && "is-busy",
                        item.matched_ordner && "is-matched-ordner",
                      )}
                      draggable={!busy}
                      disabled={busy}
                      onDragStart={(e) => {
                        onCopyDragStart();
                        e.dataTransfer.setData(POSITION_TEMPLATE_DRAG_TYPE, String(item.id));
                        e.dataTransfer.setData(POSITION_TEMPLATE_REORDER_DRAG_TYPE, String(item.id));
                        e.dataTransfer.setData("text/plain", item.name || "Vorlage");
                        e.dataTransfer.effectAllowed = "copyMove";
                        setReorderFromId(item.id);
                      }}
                      onDragEnd={() => {
                        onPaletteDragEnd();
                        setReorderFromId(null);
                        setReorderOverId(null);
                      }}
                      onClick={(e) => onApplyTemplate(item.id, e.shiftKey)}
                      title={item.beschreibung || item.name}
                    >
                      <span className="gd-template-item__icon" aria-hidden>
                        📋
                      </span>
                      <span className="min-w-0 flex-1">
                        <span className="block truncate text-sm font-semibold text-label">
                          {item.name}
                        </span>
                        <span className="mt-0.5 block text-xs text-label-secondary">
                          {item.version != null && item.version > 1 ? `v${item.version} · ` : ""}
                          {item.position_count} Position{item.position_count === 1 ? "" : "en"}
                          {item.betreff ? ` · ${item.betreff}` : ""}
                        </span>
                        {item.matched_ordner ? (
                          <span className="gd-template-item__badge">Passend zum Ordner</span>
                        ) : null}
                        {item.naechste_version ? (
                          <span className="mt-0.5 block text-xs text-accent">
                            Ab {formatVorlageGueltigAb(item.naechste_version.gueltig_ab)}: v
                            {item.naechste_version.version}
                          </span>
                        ) : null}
                        {(item.schlagworte?.length || folderCount > 0) && (
                          <span className="mt-1 flex flex-wrap gap-1">
                            {(item.schlagworte || []).slice(0, 3).map((tag) => (
                              <span key={tag} className="gd-template-chip">
                                {tag}
                              </span>
                            ))}
                            {folderCount > 0 ? (
                              <span className="gd-template-chip gd-template-chip--muted">
                                {folderCount} Ordner
                              </span>
                            ) : null}
                          </span>
                        )}
                      </span>
                      {busy ? <Spinner className="h-4 w-4 shrink-0" /> : null}
                    </button>
                    <div className="gd-template-item__actions">
                      {onEditTemplate ? (
                        <button
                          type="button"
                          className="gd-template-item__action"
                          aria-label={`Vorlage ${item.name} bearbeiten`}
                          onClick={() => onEditTemplate(item.id)}
                        >
                          ✎
                        </button>
                      ) : null}
                      <button
                        type="button"
                        className="gd-template-item__delete"
                        aria-label={`Vorlage ${item.name} löschen`}
                        onClick={() => onDeleteTemplate(item.id)}
                      >
                        ×
                      </button>
                    </div>
                  </li>
                );
              })}
            </ul>
          )}
        </section>
      ) : null}

      {showGebuchteStunden ? (
        <section className="gd-palette gd-palette--stunden">
          <p className="gd-palette__title">Bereits geleistete Stunden</p>
          <p className="gd-palette__hint">
            Zeiten, die vor dem Angebot auf dem Projekt gebucht wurden — als bereits angefallene Kosten
            übernehmen.
          </p>
          {gebuchteStundenLoading ? (
            <div className="flex items-center gap-2 py-4 text-sm text-label-secondary">
              <Spinner className="h-4 w-4" />
              Stunden werden geladen …
            </div>
          ) : !hasGebuchteStunden ? (
            <p className="py-3 text-sm text-label-secondary">
              Noch keine gebuchten Stunden für dieses Projekt.
            </p>
          ) : (
            <ul className="gd-einkauf-list">
              <li>
                <button
                  type="button"
                  className="gd-einkauf-item"
                  draggable
                  onDragStart={(e) => {
                    onCopyDragStart();
                    const payload: GebuchteStundenDragPayload = {
                      totalHours: gebuchteStundenTotal ?? 0,
                    };
                    e.dataTransfer.setData(
                      POSITION_GEBUCHTE_STUNDEN_DRAG_TYPE,
                      JSON.stringify(payload),
                    );
                    e.dataTransfer.setData("text/plain", "Bereits geleistete Stunden");
                    e.dataTransfer.effectAllowed = "copy";
                  }}
                  onDragEnd={onPaletteDragEnd}
                  onClick={onInsertGebuchteStunden}
                  title="Als Nachweisposition mit gebuchten Stunden einfügen"
                >
                  <span className="gd-einkauf-item__icon" aria-hidden>
                    ⏱
                  </span>
                  <span className="min-w-0 flex-1">
                    <span className="block truncate text-sm font-semibold text-label">
                      Bereits angefallene Kosten
                    </span>
                    <span className="mt-0.5 block text-xs text-label-secondary">
                      {formatHours(gebuchteStundenTotal ?? 0)} gebucht · per Drag oder Klick übernehmen
                    </span>
                  </span>
                </button>
              </li>
            </ul>
          )}
        </section>
      ) : null}

      {showEinkauf ? (
        <section className="gd-palette gd-palette--einkauf">
          <p className="gd-palette__title">Einkauf (Projekt)</p>
          <p className="gd-palette__hint">
            Aus Eingangsrechnungen erkannte Artikel für dieses Projekt — per Drag oder Klick in die Rechnung
            übernehmen.
          </p>

          <label className="gd-einkauf-aufschlag">
            <span className="text-xs font-medium text-label-secondary">Aufschlag auf EK</span>
            <div className="mt-1 flex items-center gap-1">
              <input
                type="text"
                inputMode="decimal"
                className="w-full rounded border border-separator bg-card px-2 py-1 text-sm tabular-nums"
                value={String(aufschlagProzent)}
                onChange={(e) => {
                  const parsed = parseAufschlagInput(e.target.value);
                  if (parsed != null) onAufschlagChange(parsed);
                }}
              />
              <span className="text-sm text-label-secondary">%</span>
            </div>
          </label>

          {einkaufLoading ? (
            <div className="flex items-center gap-2 py-4 text-sm text-label-secondary">
              <Spinner className="h-4 w-4" />
              Einkaufsartikel werden geladen …
            </div>
          ) : einkaufArtikel.length === 0 ? (
            <p className="py-3 text-sm text-label-secondary">
              Noch keine Eingangsrechnungspositionen für dieses Projekt erkannt.
            </p>
          ) : (
            <ul className="gd-einkauf-list">
              {einkaufArtikel.map((item) => {
                const key = `${item.bezeichnung}|${item.seller_article_nr || ""}|${item.ean || ""}`;
                const ek = item.einzelpreis_netto ?? 0;
                const vk = vkFromEk(ek, aufschlagProzent);
                return (
                  <li key={key}>
                    <button
                      type="button"
                      className="gd-einkauf-item"
                      draggable
                      onDragStart={(e) => {
                        onCopyDragStart();
                        e.dataTransfer.setData(
                          POSITION_EINKAUF_ARTIKEL_DRAG_TYPE,
                          buildEinkaufDragPayload(item),
                        );
                        e.dataTransfer.setData("text/plain", item.bezeichnung || "Artikel");
                        e.dataTransfer.effectAllowed = "copy";
                      }}
                      onDragEnd={onPaletteDragEnd}
                      onClick={() => onInsertEinkaufArtikel(item)}
                      title={item.bezeichnung}
                    >
                      <span className="gd-einkauf-item__icon" aria-hidden>
                        📦
                      </span>
                      <span className="min-w-0 flex-1">
                        <span className="block truncate text-sm font-semibold text-label">
                          {item.bezeichnung}
                        </span>
                        <span className="mt-0.5 block text-xs text-label-secondary">
                          Menge {formatMenge(item)}
                          {item.lieferant_firmenname ? ` · ${item.lieferant_firmenname}` : ""}
                        </span>
                        <span className="mt-0.5 block text-xs tabular-nums text-label-secondary">
                          EK {formatEuro(ek, 2)} → VK {formatEuro(vk, 2)}
                        </span>
                      </span>
                    </button>
                  </li>
                );
              })}
            </ul>
          )}
        </section>
      ) : null}
    </aside>
  );
}
